
import { supabase } from "@/integrations/supabase/client";
import { retryFailedChunks } from "./retryFailedChunks";

export interface ExportCleanupResult {
  cleaned: number;
  retriesTriggered: number;
  permanentlyFailed: number;
  message: string;
}

/**
 * Comprehensive export cleanup with retry logic
 */
export async function cleanupExportsWithRetry(): Promise<ExportCleanupResult> {  
  try {
    // First, trigger retry for failed chunks
    await retryFailedChunks();
    
    // Reset any stuck chunks that have been processing too long
    const { data: resetResult, error: resetError } = await supabase.rpc('reset_stuck_chunks', {
      p_timeout_minutes: 1 // Reset chunks stuck for more than 1 minute
    });
    
    if (resetError) {
      console.error('Error resetting stuck chunks:', resetError);
    } else {
      const resetCount = resetResult || 0;
    }
    
    // Then run the intelligent cleanup
    const { data: cleanupResults, error } = await supabase.rpc('cleanup_stuck_exports_intelligent', {
      max_age_minutes: 30
    });

    if (error) {
      console.error('Export cleanup failed:', error);
      throw error;
    }

    const results = cleanupResults || [];
    const fixed = results.filter(r => r.action_taken === 'fixed_completed').length;
    const failed = results.filter(r => r.action_taken === 'marked_failed').length;
    
    // Count permanently failed chunks (already marked by database functions)
    const { data: permanentlyFailedChunks, error: permanentError } = await supabase
      .from('csv_export_chunks')
      .select('id')
      .gte('retry_count', 3)
      .eq('status', 'failed');
    
    if (permanentError) {
      console.error('Error counting permanently failed chunks:', permanentError);
    }

    const permanentlyFailed = permanentlyFailedChunks?.length || 0;
    const resetCount = resetResult || 0;

    const message = `Cleanup completed: ${fixed} exports fixed, ${failed} exports marked as failed, ${permanentlyFailed} chunks permanently failed, ${resetCount} stuck chunks reset, retries triggered for recoverable failures.`;
        
    return {
      cleaned: fixed + failed + resetCount,
      retriesTriggered: 1, // We triggered one retry cycle
      permanentlyFailed,
      message
    };
    
  } catch (error) {
    console.error('Export cleanup with retry failed:', error);
    throw error;
  }
}

/**
 * Monitor export health and trigger retries as needed
 */
export async function monitorExportHealth(): Promise<void> {  
  try {
    // Reset stuck chunks first
    const { data: resetCount, error: resetError } = await supabase.rpc('reset_stuck_chunks', {
      p_timeout_minutes: 1
    });
    
    if (resetError) {
      console.error('Error resetting stuck chunks:', resetError);
    } 
    
    // Get exports that might need attention
    const { data: exports, error } = await supabase
      .from('csv_exports')
      .select('export_id, status, chunks_completed, chunk_count, created_at')
      .in('status', ['processing', 'failed'])
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) {
      console.error('Error monitoring export health:', error);
      return;
    }

    for (const exportData of exports || []) {
      const exportAge = new Date().getTime() - new Date(exportData.created_at).getTime();
      const ageInMinutes = exportAge / (1000 * 60);
      
      // If export is older than 10 minutes and not completed, check for retryable chunks
      if (ageInMinutes > 10 && exportData.status === 'processing') {        
        try {
          await retryFailedChunks(exportData.export_id);
        } catch (error) {
          console.error(`Error retrying chunks for export ${exportData.export_id}:`, error);
        }
      }
    }
    
  } catch (error) {
    console.error('Export health monitoring failed:', error);
  }
}
